import styles from "./EmailRegistration.module.css";

import React, { useState } from "react";

import { Button, Checkbox, Grid, TextField } from "@material-ui/core";

import GlobalMessage from "../../GlobalMessage";
import LoadingMsgBar from "../../LoadingMsgBar";

import { isEmailAddress, isNotEmpty } from "../../../utils/Validator.js";

import RegistrationService from "../../../Services/RegistrationService";

function EmailRegistration({ history }) {
  const [viewState, setViewState] = useState("FORM_ACTIVE");
  const [emailAddress, setEmailAddress] = useState("");
  const [emailAddressError, setEmailAddressError] = useState("");
  const [areTermsAccepted, setAreTermsAccepted] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [processingMessage, setProcessingMessage] = useState(null);
  const [disableSubmit, setDisableSubmit] = useState(false);

  const handleTermsAccepted = (event) => {
    setAreTermsAccepted(event.target.checked);
  };

  /**
   * Checks if an email address belongs to one of the internal domains
   *
   * @param {string} email - The email address to check
   * @returns {boolean} - True if the email belongs to an internal domain, false otherwise
   */
  const isInternalEmail = (email) => {
    const INTERNAL_DOMAINS = [
      "cvty.us.com",
      "enlyte.com",
      "genex.com",
      "mitchell.com",
    ];

    // Return false if email is not provided or not a string
    if (!email || typeof email !== "string") {
      return false;
    }

    // Extract the domain part of the email
    const emailParts = email.trim().toLowerCase().split("@");

    // Check if the email has a valid format (contains exactly one @ symbol)
    if (emailParts.length !== 2) {
      return false;
    }

    const domain = emailParts[1];

    // Check if the domain matches any of the internal domains
    return INTERNAL_DOMAINS.includes(domain);
  };

  const validateForm = () => {
    if (!isEmailAddress(emailAddress)) {
      setEmailAddressError("A valid Email Address is required.");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setErrorMessage(null);

    if (validateForm()) {
      if (isInternalEmail(emailAddress)) {
        setViewState("INTERNAL_USER");
        return;
      } else {
        try {
          setDisableSubmit(true);
          setProcessingMessage("Please wait while we process your request.");
          await RegistrationService.submitRequestAccountEmail(emailAddress);

          setViewState("COMPLETE");
        } catch (error) {
          setErrorMessage(
            `There was an error processing your request. Please try again. If it persists, please email Coventry Provider Portal Support at <a
            href="mailto:coventryprovidertechsupport@cvty.us.com?subject=Coventryprovider.com%20Account%20Request%20-%20Submission%20Error"
          >coventryprovidertechsupport@cvty.us.com</a>.`
          );
          console.log(error);
        } finally {
          setProcessingMessage(null);
          setDisableSubmit(false);
        }
      }
    }
  };

  const handleCancel = () => {
    history.push("/wcpp/login");
  };

  const renderEmailAddressForm = () => {
    return (
      <div className={styles.emailRegistration}>
        <h1 className="App-page-label">
          Coventry Provider Portal Account Request
        </h1>

        <LoadingMsgBar
          style={{ marginBottom: "1rem" }}
          loadMsg={processingMessage}
        />

        <GlobalMessage
          style={{ marginBottom: "1rem" }}
          message={errorMessage}
          type="error"
        />

        <div>
          <p>
            If you have a Provider Portal Account Administrator for your group,
            ask them to create an account for you. Otherwise, please enter your
            email address.
          </p>

          <div className="App-error-font App-error-color App-note-Field">
            Note: Fields marked with an asterisk(*) are required.
          </div>

          <Grid container direction="column">
            <Grid item xs={12} md={6}>
              <TextField
                id="emailAddress"
                name="emailAddress"
                label="Email Address"
                error={isNotEmpty(emailAddressError)}
                helperText={emailAddressError}
                value={emailAddress}
                onChange={(e) => {
                  setEmailAddress(e.target.value);
                }}
                inputProps={{ maxLength: 120 }}
                fullWidth
                autoFocus
                required
              />
            </Grid>
          </Grid>

          <div className="centerBox-vertical">
            <Checkbox
              className="centerItem-vertical"
              id="termsAndConditions"
              checked={areTermsAccepted}
              onChange={handleTermsAccepted}
            />

            <label htmlFor="termsAndConditions" className="centerItem-vertical">
              By using the Website I agree to these{" "}
              <a
                href="https://www.enlyte.com/terms-use"
                className="App-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                terms and conditions.
              </a>
            </label>
          </div>
        </div>

        <div className="buttonBar">
          <Button
            disabled={!areTermsAccepted || disableSubmit}
            onClick={handleSubmit}
            aria-label="Coventry Provider Portal Account Request: SUBMIT Button"
          >
            Submit
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancel}
            aria-label="Coventry Provider Portal Account Request: CANCEL Button"
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  const renderInternalUser = () => {
    return (
      <div className="registration">
        <h1 className="App-page-label">
          Coventry Provider Portal Account Request
        </h1>

        <div className="step-main">
          <p>
            Your email address indicates you are an internal user. Internal
            users should submit an access request via ServiceNow. If you need
            help please email Coventry Provider Portal Support at{" "}
            <a href="mailto:coventryprovidertechsupport@cvty.us.com?subject=Coventryprovider.com%20Account%20Request%20-%20Submission%20Internal%20Help">
              coventryprovidertechsupport@cvty.us.com
            </a>
            .
          </p>

          <div className="buttonBar">
            <Button onClick={handleCancel}>Done</Button>
          </div>
        </div>
      </div>
    );
  };

  const renderThankYou = () => {
    return (
      <div className="registration">
        <h1 className="App-page-label">
          Coventry Provider Portal Account Request
        </h1>

        <div className="step-main">
          <p>
            Your account request has been received. You will receive an email
            shortly with instructions to complete the registration process. If
            you have not received the email within one business day please email
            Coventry Provider Portal Support at{" "}
            <a href="mailto:coventryprovidertechsupport@cvty.us.com?subject=Coventryprovider.com%20Account%20Request%20-%20Missing%20Email%20Registration%20Code">
              coventryprovidertechsupport@cvty.us.com
            </a>
            .
          </p>

          <div className="buttonBar">
            <Button onClick={handleCancel}>Done</Button>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (viewState) {
      case "FORM_ACTIVE":
        return renderEmailAddressForm();
      case "INTERNAL_USER":
        return renderInternalUser();
      case "COMPLETE":
        return renderThankYou();
      default:
        return <div>Something went wrong. Please try again.</div>;
    }
  };

  return renderContent();
}

export { EmailRegistration };
